import {
  Basket,
  BasketSchema,
  BasketItem,
  CarHireBasketOffer,
  HotelBasketOffer,
  ExperienceBasketOffer,
  FlightBasketOffer,
} from '@reward-platform/order-schemas/basket'
import type { CountryCodes, CurrencyCodes, PlatformThemeId } from '@reward-platform/platform-config'
import { HEADER_KEYS } from '@reward-platform/shared-api/headers'
import { PlatformError } from '~/utils/errors'
import { getBasketItemTypesToClearOnPlatformSwitch } from '~/utils/basketPlatformSwitch'
import { createServiceSchemaValidator } from './utils'
import { getClientRequestHeader, nextApiClient } from './clients/nextApiClient'

const validateBasket = createServiceSchemaValidator(BasketSchema)

export type BasketRequestContext = {
  countryCode: CountryCodes
  currencyCode: CurrencyCodes
}

const getRequestOptions = (requestContext?: BasketRequestContext) =>
  requestContext
    ? {
        headers: {
          [HEADER_KEYS.countryCode]: requestContext.countryCode,
          [HEADER_KEYS.currencyCode]: requestContext.currencyCode,
        },
      }
    : undefined

export const getBasket = async (
  resolveLocations?: boolean,
  requestContext?: BasketRequestContext
): Promise<Basket> => {
  const countryCode = requestContext?.countryCode ?? getClientRequestHeader('countryCode')
  const currencyCode = requestContext?.currencyCode ?? getClientRequestHeader('currencyCode')

  if (!countryCode || !currencyCode) {
    return Promise.reject(new PlatformError(`The request headers are not properly set.`))
  }

  const urlSearchParams = new URLSearchParams()

  if (resolveLocations) {
    urlSearchParams.append('resolveLocations', resolveLocations.toString())
  }

  const query = urlSearchParams.toString()
  const queryString = query.length ? `?${query}` : ''

  const requestOptions = getRequestOptions(requestContext)
  const { data } = requestOptions
    ? await nextApiClient.get(`/basket/items${queryString}`, requestOptions)
    : await nextApiClient.get(`/basket/items${queryString}`)
  return validateBasket(data)
}

export const addHotelToBasket = async (
  offer: HotelBasketOffer,
  requestContext?: BasketRequestContext
) => {
  const requestOptions = getRequestOptions(requestContext)
  if (requestOptions) {
    await nextApiClient.post(`/basket/add/hotel`, offer, requestOptions)
    return
  }
  await nextApiClient.post(`/basket/add/hotel`, offer)
}

export const addCarHireToBasket = async (
  offer: CarHireBasketOffer,
  requestContext?: BasketRequestContext
) => {
  const requestOptions = getRequestOptions(requestContext)
  if (requestOptions) {
    await nextApiClient.post(`/basket/add/car-hire`, offer, requestOptions)
    return
  }
  await nextApiClient.post(`/basket/add/car-hire`, offer)
}

export const addExperienceToBasket = async (
  offer: ExperienceBasketOffer,
  requestContext?: BasketRequestContext
) => {
  const requestOptions = getRequestOptions(requestContext)
  if (requestOptions) {
    await nextApiClient.post(`/basket/add/experience`, offer, requestOptions)
    return
  }
  await nextApiClient.post(`/basket/add/experience`, offer)
}

export const addFlightToBasket = async (
  offer: FlightBasketOffer,
  requestContext?: BasketRequestContext
) => {
  const requestOptions = getRequestOptions(requestContext)
  if (requestOptions) {
    await nextApiClient.post(`/basket/add/flight`, offer, requestOptions)
    return
  }
  await nextApiClient.post(`/basket/add/flight`, offer)
}

export const removeItemFromBasket = async (
  recommendationId: string,
  requestContext?: BasketRequestContext
) => {
  const requestOptions = getRequestOptions(requestContext)
  if (requestOptions) {
    await nextApiClient.delete(`/basket/items/${recommendationId}`, requestOptions)
    return
  }
  await nextApiClient.delete(`/basket/items/${recommendationId}`)
}

export const clearItemsFromBasket = async (requestContext?: BasketRequestContext) => {
  const requestOptions = getRequestOptions(requestContext)
  if (requestOptions) {
    await nextApiClient.delete(`/basket/items`, requestOptions)
    return
  }
  await nextApiClient.delete(`/basket/items`)
}

export const clearBasketItemsOnPlatformSwitch = async (
  theme: PlatformThemeId,
  requestContext?: BasketRequestContext
): Promise<{
  basket: Basket
  removedItemIds: string[]
}> => {
  const basket = await getBasket(false, requestContext)
  const itemTypesToClear = getBasketItemTypesToClearOnPlatformSwitch(theme)
  const itemsToClear: BasketItem[] = []

  itemTypesToClear.forEach((itemType) => {
    itemsToClear.push(...basket.items[itemType])
  })

  if (itemsToClear.length === 0) {
    return { basket, removedItemIds: [] }
  }

  const removedItemIds = itemsToClear.map(({ id }) => {
    if (!id) {
      throw new PlatformError('Unable to remove platform switch basket item without an item id.')
    }

    return id
  })

  await Promise.all(removedItemIds.map((id) => removeItemFromBasket(id, requestContext)))

  return {
    basket: {
      ...basket,
      items: {
        ...basket.items,
        ...itemTypesToClear.reduce(
          (cleanedItems, itemType) => ({
            ...cleanedItems,
            [itemType]: [],
          }),
          {} as Partial<Basket['items']>
        ),
      },
    },
    removedItemIds,
  }
}
